/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

// Needed for redux-saga es6 generator support
import 'core-js/features/promise';
import 'regenerator-runtime/runtime';

import { Suspense, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Security } from '@okta/okta-react';
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { createRoutes } from '@app/routes';
import { createAuthLoader } from '@app/loaders/authLoader';

import { Provider } from 'react-redux';

import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';

import {
  asyncWithLDProvider,
  withLDConsumer,
} from 'launchdarkly-react-client-sdk';

import MaintenancePage from '@app/containers/MaintenancePage';

import * as analytics from '@app/helpers/analytics';
import theme from '@app/theme';

import SnackbarProvider from '@app/containers/SnackbarProvider';
import I18nToggle from '@app/components/I18nToggle';
import PageImportFallback from '@app/components/PageImportFallback';

import { isUnsupportedBrowser } from '@app/helpers/browser';

import store from '@app/configureStore';
import UnsupportedBrowser from '@app/components/UnsupportedBrowser';
import { getOktaConfig } from '@app/okta.config';
import { getEnvConfig } from '@app/env.config';
import { shouldIgnoreError } from '@app/helpers/errorFiltering';
import {
  clearChunkErrorReloaded,
  installChunkErrorReloadHandler,
} from '@app/utils/loadable';
import Notifier from '@app/containers/Notifier';

const { ENVIRONMENT } = getEnvConfig();

const MOUNT_NODE = document.getElementById('app');

const MaintenanceGate = withLDConsumer()(({ flags, children }) => {
  return flags.maintenancePage ? <MaintenancePage /> : children;
});

const AppContent = ({ oktaAuth, router, restoreOriginalUri }) => {
  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <SnackbarProvider>
        <Notifier />
        <MaintenanceGate>
          <RouterProvider router={router} />
        </MaintenanceGate>
      </SnackbarProvider>
    </Security>
  );
};

let sentryCreateBrowserRouter = null;

const root = createRoot(MOUNT_NODE);

const render = async () => {
  const isUnsupported = isUnsupportedBrowser();
  const LDProvider = await asyncWithLDProvider({
    clientSideID: getEnvConfig().LAUNCHDARKLY_CLIENT_ID,
  });

  if (import.meta.env.VITE_ENABLE_MSW === 'true') {
    const { worker } = await import('./mocks/browser');
    await worker.start({
      onUnhandledRequest: 'bypass',
    });
  }

  const oktaConfig = getOktaConfig();
  const oktaAuth = new OktaAuth(oktaConfig.oidc);

  const authLoader = createAuthLoader(oktaAuth);
  const routes = createRoutes({ authLoader });

  const routerFactory = sentryCreateBrowserRouter || createBrowserRouter;
  const router = routerFactory(routes);

  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    const relativeUrl = toRelativeUrl(originalUri || '/', window.location.origin);
    router.navigate(relativeUrl);
  };

  root.render(
    <Provider store={store}>
      <LDProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {ENVIRONMENT !== 'Production' && <I18nToggle />}
          {isUnsupported ? (
            <UnsupportedBrowser />
          ) : (
            <Suspense fallback={<PageImportFallback />}>
              <AppContent
                oktaAuth={oktaAuth}
                router={router}
                restoreOriginalUri={restoreOriginalUri}
              />
            </Suspense>
          )}
        </ThemeProvider>
      </LDProvider>
    </Provider>,
  );
};

const SENTRY_TRACES_SAMPLE_RATE_BY_ENVIRONMENT = {
  Production: 0.05,
};

const getSentryTracesSampleRate = environment =>
  SENTRY_TRACES_SAMPLE_RATE_BY_ENVIRONMENT[environment] ?? 0.2;

const initializeSentry = async () => {
  const envConfig = getEnvConfig();
  if (!envConfig.SENTRY_DSN || !ENVIRONMENT || ENVIRONMENT === 'Local') {
    return;
  }

  const Sentry = await import('@sentry/react');

  Sentry.init({
    dsn: envConfig.SENTRY_DSN,
    environment: ENVIRONMENT,
    ...(envConfig.VERSION?.trim() && {
      release: `bennie-web@${envConfig.VERSION.trim()}`,
    }),
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: getSentryTracesSampleRate(ENVIRONMENT),
    beforeSendTransaction(event) {
      if (
        !ENVIRONMENT ||
        ENVIRONMENT === 'Local' ||
        event.transaction === '/implicit/callback'
      ) {
        return null;
      }
      return event;
    },
    beforeSend(event, hint) {
      const originalError = hint.originalException;

      if (shouldIgnoreError(originalError)) {
        return null;
      }

      if (!ENVIRONMENT || ENVIRONMENT === 'Local') {
        return null;
      }

      const sanitizedEvent = { ...event };
      delete sanitizedEvent.user;
      return sanitizedEvent;
    },
  });

  sentryCreateBrowserRouter =
    Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);
};

analytics.init(getEnvConfig().MIXPANEL_TOKEN, {
  debug: ENVIRONMENT !== 'Production',
});

installChunkErrorReloadHandler();

(async () => {
  try {
    await initializeSentry();
  } catch (error) {
    console.error('Failed to initialize Sentry:', error);
  }
  try {
    await render();
    clearChunkErrorReloaded();
  } catch (error) {
    console.error('Failed to render app:', error);
  }
})();
